import React from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import { PrismicRichText } from "@prismicio/react";
import { GatsbyImage } from "gatsby-plugin-image";
import { use100vh } from "react-div-100vh";

// Previews
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

// Utils
import { FormatDate } from "../components/utils/format-date";
import kebabCase from "lodash.kebabcase";

// SEO
import { PageSeo } from "../components/seo/page-seo";
import { Video } from "../components/video/video";
import { useEffect } from "react";

const Page = styled.div`
  padding: 250px 0 0 0;

  /* & .content-container {
    max-width: 910px;
    margin: 0 auto;
  } */

  & article {
    display: grid;
    grid-template-columns: 2fr 153px 3fr;
    gap: 30px;
    padding: 0 30px;

    border-bottom: 1px solid #000;
    &:first-of-type {
      border-top: 1px solid #000;
    }

    @media (hover: hover) {
      &:hover {
        background: white;
      }
    }

    & .title-container {
      padding: 30px 0;

      & span {
        display: none;
      }
    }

    & .image-container {
      position: relative;
      aspect-ratio: 1/1;

      & .gatsby-image-wrapper {
        width: 100%;
        height: 100%;
        aspect-ratio: 1/1;
      }

      & img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        aspect-ratio: 1/1;
      }
    }

    & .text-container {
      padding: 30px 0;

      & p {
        &:first-of-type {
          margin-top: 0;
        }

        &:last-of-type {
          margin-bottom: 0;
        }
      }

      & a {
        color: #000;
        text-decoration: underline;
        text-decoration-thickness: 2px;
        text-underline-offset: 3px;

        &::after {
          content: " ↗";
        }

        &:hover {
          color: #999999;
        }
      }
    }
  }

  @media (max-width: 860px) {
    padding: 60px 0 0 0;

    & article {
      grid-template-columns: 1fr;
      gap: 0;
      padding: 20px;

      &:first-of-type {
        border-top: 0;
      }

      & .image-container {
        order: 1;

        aspect-ratio: auto;

        & .gatsby-image-wrapper {
          width: 100%;
          height: 100%;
          aspect-ratio: auto;
        }

        & img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          aspect-ratio: auto;
        }
      }

      & .title-container {
        order: 2;
        margin: 1em 0 0 0;
        padding: 0;

        display: grid;
        grid-template-columns: 50px 1fr;

        & span {
          display: block;
        }
      }

      & .inner-text-container {
        order: 3;

        & .text-container {
          padding: 1em 0 0 0;
        }

        & a {
          text-decoration-thickness: 1px;
          text-underline-offset: 2px;
        }
      }
    }
  }
`;

const News = ({ data, location }) => {
  // Window Height
  const height = use100vh();

  useEffect(() => {
    if (
      location !== undefined &&
      location !== null &&
      location.state !== undefined &&
      location.state !== null &&
      location.state.article !== undefined &&
      location.state.article !== null
    ) {
      const timer = setTimeout(() => {
        const articleToScrollTo = document.getElementById(
          `article-${location.state.article}`
        );

        if (articleToScrollTo !== undefined) {
          articleToScrollTo.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location]);

  const content = data.allPrismicNewsArticle.nodes.map((article, index) => {
    const innerContent = article.data.body.map((item, index) => {
      if (item.slice_type === `image`) {
        return (
          <div className="image-container">
            {item.primary.image !== null && (
              <GatsbyImage
                image={item.primary.image.gatsbyImageData}
                alt={
                  item.primary.image.alt !== null ? item.primary.image.alt : ``
                }
              />
            )}
          </div>
        );
      }

      if (item.slice_type === `video`) {
        return (
          <div className="video-container">
            <Video video={item.primary.video} id={item.id} />
          </div>
        );
      }

      if (item.slice_type === `text`) {
        return (
          <div className="text-container">
            <PrismicRichText field={item.primary.text.richText} />
          </div>
        );
      }
    });

    return (
      <>
        <div id={`article-${article.uid}`} />
        <article key={`single_article_${index}_${article.id}`}>
          <div className="title-container">
            <span>{index + 1}</span>
            <div className="title-container-inner">
              <PrismicRichText field={article.data.title.richText} />
              {FormatDate(article.data.published_date, null)}
            </div>
          </div>

          <div className="image-container">
            <GatsbyImage
              image={article.data.thumbnail.gatsbyImageData}
              alt={article.data.thumbnail.alt}
            />
          </div>

          <div className="inner-text-container">{innerContent}</div>
        </article>
      </>
    );
  });

  return (
    <>
      <PageSeo
        seoTitle={data.prismicNews.data.title.text}
        seoText={null}
        seoImage={null}
      />

      <Page height={height}>
        <div className="content-container">{content}</div>
      </Page>
    </>
  );
};

export default withPrismicPreview(News);

export const query = graphql`
  query {
    prismicNews {
      data {
        title {
          text
        }
      }
    }
    allPrismicNewsArticle(
      sort: { fields: data___published_date, order: DESC }
    ) {
      nodes {
        id
        uid
        data {
          title {
            richText
            text
          }
          published_date
          thumbnail {
            alt
            gatsbyImageData
          }
          body {
            ... on PrismicNewsArticleDataBodyImage {
              id
              slice_type
              primary {
                image {
                  alt
                  gatsbyImageData
                }
                caption {
                  richText
                }
              }
            }
            ... on PrismicNewsArticleDataBodyText {
              id
              slice_type
              primary {
                text {
                  richText
                }
              }
            }
            ... on PrismicNewsArticleDataBodyVideo {
              id
              slice_type
              primary {
                video {
                  id
                  html
                }
                caption {
                  richText
                }
              }
            }
          }
        }
      }
    }
  }
`;
